// Import the zustand store instance
import {
  deformatDateT,
  deformatMonthT,
  deformatYearT,
  formatDateT,
} from "../../../shared/utils/formatters";
import { TAXI_QUOTE_SOB } from "../../../config/config";
import useQuoteStore from "../store/QuoteStore";
import { updateQuoteFields } from "../../../shared/utils/storeUtils";

const Tq5Mapper = {
  get() {
    const vehicle = useQuoteStore.getState().quote?.risk?.vehicles?.[0] ?? {};
    const vehicles = useQuoteStore.getState().quote?.risk?.vehicles ?? [];
    const quote = useQuoteStore.getState().quote?.risk;

    const securityDevice = vehicle?.securityDevices?.[0] ?? null;
    const modification = vehicle?.modifications?.[0] ?? null;
    const garageAddress = vehicle?.garageAddress ?? {};
    // Initialize lookupvehicleReg based on existing vehicle data
    const lookupvehicleReg =
      vehicles?.length > 0 && vehicle.make ? vehicle : false;

    if (Object.keys(lookupvehicleReg).length > 0) {
      lookupvehicleReg.abiVehicleInfoDesc ??= [
        vehicle.yearOfManufacture,
        `${vehicle.cubicCapacity}cc`,
        `${vehicle.noOfSeats} seats`,
      ].join(", ");
    }
    return {
      // Vehicle Registration and Basic Details
      manualVehicleReg: {
        abiMake: vehicle?.make,
        abiModel: vehicle?.model,
        abiDoors:
          vehicle?.doors ??
          (vehicle?.noOfSeats % 2 === 0
            ? vehicle?.noOfSeats
            : vehicle?.noOfSeats - 1),
        abiCC: vehicle?.cubicCapacity,
        abiVehicleInfoDesc: vehicle?.abiVehicleInfoDesc,
      },
      licensingPlateNumber:
        vehicle?.registrationNumber ?? vehicle?.licensingPlateNumber ?? null,
      VehicleReg:
        vehicle?.registrationNumber ?? vehicle?.licensingPlateNumber ?? null,
      seats: vehicle?.noOfSeats?.toString() ?? null,
      vehicleYear:
        vehicle?.yearOfManufacture && vehicle.yearOfManufacture !== 1899
          ? { value: vehicle.yearOfManufacture }
          : null,
      lookupvehicleReg: lookupvehicleReg || null,
      lookupData: vehicle?.licensingPlateNumber ? true : false,

      // Vehicle Status
      vehImported:
        vehicle?.imported === null
          ? null
          : vehicle?.imported === true
          ? "Yes"
          : "No",
      rightHandDrive:
        vehicle?.rightHandDrive === null
          ? null
          : vehicle?.rightHandDrive === true
          ? "Yes"
          : "No",
      vehicleModified:
        vehicle?.selfModified === null
          ? null
          : vehicle?.selfModified === true
          ? "Yes"
          : "No",
      vehicleQPlated:
        vehicle?.qPlated === null
          ? null
          : vehicle?.qPlated === true
          ? "Yes"
          : "No",
      dashCam:
        vehicle?.interiorCamera == null
          ? null
          : vehicle.interiorCamera
          ? "Yes"
          : "No",

      // Lookup Related
      vehicleDetailsCorrect:
        vehicle?.interiorCamera != null
          ? vehicle?.interiorCamera === true
            ? "Yes"
            : "No"
          : null,
      prevVehicleModified:
        vehicle?.selfModified === null
          ? null
          : vehicle?.selfModified === true
          ? "Yes"
          : "No",
      machedData: vehicle?.interiorCamera !== null ? true : false,
      valuesMached: vehicle?.interiorCamera !== null ? true : false,
      noMatchedData: vehicle?.interiorCamera !== null ? true : false,
      prevLookupData: vehicle?.previousVehicleYOM ?? null,

      // Security Devices
      security: vehicle?.securityDevices
        ? vehicle?.securityDevices?.length > 0
          ? "Yes"
          : "No"
        : null,
      securityDevice: securityDevice?.securityDeviceType
        ? { value: securityDevice.securityDeviceType }
        : null,

      // Modifications
      modifications: modification?.vehicleModificationId
        ? { value: modification.vehicleModificationId }
        : null,

      // Previous Vehicle Details
      vehRegistration:
        vehicle?.havePreviousRegistration === null
          ? null
          : vehicle?.havePreviousRegistration === false
          ? "No"
          : "Yes",
      prevVehicleRegis: vehicle?.previousVehicleRegistrationNumber ?? null,
      ownedPrevVehicle:
        vehicle?.previousVehicleOwned12Month === true ? "Yes" : "No",
      prevVehicleAbiCode: vehicle?.previousVehicleAbicode ?? null,
      prevVehicleYear: vehicle?.previousVehicleYOM
        ? { value: vehicle.previousVehicleYOM.toString() }
        : null,
      prevVehicleMake: vehicle?.previousVehicleMake ?? null,
      prevVehicleModel: vehicle?.previousVehicleModel ?? null,
      prevLookupVehicleReg: vehicle?.previousVehicleMake
        ? {
            abiCode: vehicle.previousVehicleAbicode ?? null,
            make: vehicle.previousVehicleMake ?? null,
            model: vehicle.previousVehicleModel ?? null,
            cubicCapacity: vehicle.previousVehicleCubicCapacity ?? null,
            abiVehicleInfoDesc:
              [
                vehicle.previousVehicleYOM
                  ? vehicle.previousVehicleYOM.toString()
                  : "",
                vehicle.previousVehicleCubicCapacity
                  ? `${vehicle.previousVehicleCubicCapacity}cc`
                  : "",
                vehicle.previousVehicleSeats
                  ? `${vehicle.previousVehicleSeats} seats`
                  : "",
                vehicle.previousVehicleBodyTypeId ?? "",
                vehicle.previousVehicleFuelTypeId ?? "",
                vehicle.previousVehicleGearboxId ?? "",
              ]
                .filter(Boolean)
                .join(", ") || null,
          }
        : null,

      // Basic Identifiers
      vMake: { value: vehicle?.make || null },
      vModel: { value: vehicle?.model || null },
      make: { value: vehicle?.make || null },
      model: { value: vehicle?.model || null },

      // Doors and Year
      abiDoors:
        vehicle?.doors ??
        (vehicle?.noOfSeats % 2 === 0
          ? vehicle?.noOfSeats
          : vehicle?.noOfSeats - 1),
      vehicleDoor:
        vehicle?.doors ??
        (vehicle?.noOfSeats % 2 === 0
          ? vehicle?.noOfSeats
          : vehicle?.noOfSeats - 1),
      yearOfManufacture: vehicle?.yearOfManufacture
        ? { value: vehicle.yearOfManufacture }
        : null,

      // Garage Address
      garageAddress: {
        houseNo: garageAddress?.houseNo ?? "",
        postcode: garageAddress?.postcode ?? "",
        street: garageAddress?.street ?? "",
        county: garageAddress?.county ?? "",
        locality: garageAddress?.locality ?? "",
      },

      // Purchase Dates
      purchaseDateMonth:
        vehicle?.purchaseDate &&
        (deformatMonthT(vehicle?.purchaseDate) === "30/12/1899" ||
          vehicle?.purchaseDate === quote?.startDate)
          ? null
          : vehicle?.purchaseDate
          ? { value: deformatMonthT(vehicle?.purchaseDate) }
          : null,

      purchaseDateYear:
        vehicle?.purchaseDate &&
        (deformatYearT(vehicle?.purchaseDate) === "30/12/1899" ||
          vehicle?.purchaseDate === quote?.startDate)
          ? null
          : vehicle?.purchaseDate
          ? { value: deformatYearT(vehicle?.purchaseDate) }
          : null,

      // Purchase Info
      vehiclePurchased:
        vehicle?.purchaseDate === null
          ? null
          : vehicle?.purchaseDate !== quote?.startDate
          ? "Yes"
          : "No",

      // ABI & CC
      abiCode: vehicle?.abiCode ?? null,
      cubicCapacity: vehicle?.cubicCapacity
        ? `${vehicle.cubicCapacity}cc`
        : null,
    };
  },

  // Setter function to update the vehicle data in the store
  set(data, STATE) {
    const quote = useQuoteStore.getState().quote;
    const vehicle = quote?.risk?.vehicles?.[0];
    const driver = quote?.risk?.drivers?.[0];
    const purchaseDate =
      data.purchaseDateYear && data.purchaseDateMonth
        ? `${data.purchaseDateYear.value}/${data.purchaseDateMonth.value}/01`
        : null;

    // Basic Vehicle Details
    updateQuoteFields({
      "risk.vehicles.0.licensingPlateNumber": data.VehicleReg,
      "risk.vehicles.0.registrationNumber": data.VehicleReg,
      "risk.vehicles.0.noOfSeats": data.seats ? +data.seats : null,
      "risk.vehicles.0.imported": data.vehImported === "Yes",
      "risk.vehicles.0.rightHandDrive": data.rightHandDrive === "Yes",
      "risk.vehicles.0.selfModified": data.vehicleModified === "Yes",
      "risk.vehicles.0.qPlated": data.vehicleQPlated === "Yes",
      "risk.vehicles.0.interiorCamera": data.dashCam === "Yes",
      "risk.sourceOfBusinessId": TAXI_QUOTE_SOB,
    });

    // yearOfManufacture: only set if present, no default 2008
    const yearOfManufacture =
      data.lookupvehicleReg?.year ??
      data.vehicleYear?.value ??
      data?.yearOfManufacture?.value;
    if (yearOfManufacture) {
      updateQuoteFields({
        "risk.vehicles.0.yearOfManufacture": yearOfManufacture,
      });
    }

    // Vehicle Lookup Data (manualVehicleReg)
    if (data.manualVehicleReg) {
      const manualVehicleData = data?.manualVehicleReg || "";
      if (manualVehicleData) {
        updateQuoteFields({
          "risk.vehicles.0.preVehicleSearch": null,
          "risk.vehicles.0.preVehicleSearchSuccess": null,
          "risk.vehicles.0.abiVehicleInfoDesc":
            manualVehicleData.abiVehicleInfoDesc ??
            vehicle.abiVehicleInfoDesc ??
            null,
          "risk.vehicles.0.doors": manualVehicleData.abiDoors ?? null,
          "risk.vehicles.0.abiCode": manualVehicleData.abiCode,
          "risk.vehicles.0.make":
            manualVehicleData.abiMake ?? vehicle?.make ?? "",
          "risk.vehicles.0.model":
            manualVehicleData.abiModel ?? vehicle?.model ?? "",
          "risk.vehicles.0.cubicCapacity":
            manualVehicleData.abiCC ?? vehicle?.cubicCapacity ?? "",
          "risk.vehicles.0.bodyTypeId": manualVehicleData.bodyTypeId || "04",
          "risk.vehicles.0.fuelTypeId": manualVehicleData.abiFuel || "006",
          "risk.vehicles.0.gearboxId": manualVehicleData.abiShift || "001",
        });
      }
    }

    // Vehicle Lookup Data (lookupvehicleReg)
    if (data.lookupvehicleReg) {
      const vehicleData = data.lookupvehicleReg;
      updateQuoteFields({
        "risk.vehicles.0.abiVehicleInfoDesc":
          vehicleData.abiVehicleInfoDesc ?? vehicle.abiVehicleInfoDesc ?? null,
        "risk.vehicles.0.abiCode": vehicleData.abiCode,
        "risk.vehicles.0.make": vehicleData.abiMake ?? vehicle?.make ?? "",
        "risk.vehicles.0.model": vehicleData.abiModel ?? vehicle?.model ?? "",
        "risk.vehicles.0.cubicCapacity":
          vehicleData.abiCC ?? vehicle?.cubicCapacity ?? "",
        "risk.vehicles.0.bodyTypeId": vehicleData.bodyTypeId || "04",
        "risk.vehicles.0.fuelTypeId": vehicleData.abiFuel || "006",
        "risk.vehicles.0.gearboxId": vehicleData.abiShift || "001",
      });
    }

    // Security Devices
    if (data.security === "Yes" && data.securityDevice?.value) {
      updateQuoteFields({
        "risk.vehicles.0.securityDevices": [
          {
            typeId: "3",
            subTypeId: "0",
            dateFitted: "2022-02-01T00:00:00",
            securityDeviceType: data.securityDevice.value,
            isDeleted: false,
            objectId: "",
            objectHistoryId: 0,
            parentObjectId: "",
            parentObjectHistoryId: 0,
          },
        ],
      });
    } else {
      updateQuoteFields({ "risk.vehicles.0.securityDevices": [] });
    }

    // Modifications
    if (data.vehicleModified === "Yes" && data.modifications?.value) {
      updateQuoteFields({
        "risk.vehicles.0.modifications": [
          {
            typeId: "4",
            subTypeId: 0,
            isDeleted: false,
            isStandard: false,
            description: "",
            vehicleModificationId: data.modifications.value,
            objectId: "",
            objectHistoryId: 0,
            parentObjectId: "",
            parentObjectHistoryId: 0,
          },
        ],
      });
    } else {
      updateQuoteFields({ "risk.vehicles.0.modifications": [] });
    }

    // Previous Vehicle Details
    updateQuoteFields({
      "risk.vehicles.0.havePreviousRegistration":
        data.vehRegistration === null ? false : data.vehRegistration === "Yes",
    });
    if (data.vehRegistration === "Yes") {
      updateQuoteFields({
        "risk.vehicles.0.previousVehicleRegistrationNumber":
          data.prevVehicleRegis,
        "risk.vehicles.0.previousVehicleOwned12Month":
          data.ownedPrevVehicle === "Yes",
        "risk.vehicles.0.previousVehicleAbicode":
          data.prevLookupVehicleReg?.abiCode,
        "risk.vehicles.0.previousVehicleYOM": data.prevVehicleYear?.value,
        "risk.vehicles.0.previousVehicleMake":
          data.prevLookupVehicleReg?.make ?? data.prevVehicleMake,
        "risk.vehicles.0.previousVehicleModel":
          data.prevLookupVehicleReg?.model ?? data.prevVehicleModel ?? null,
        "risk.vehicles.0.previousVehicleCubicCapacity":
          data.prevLookupVehicleReg?.cubicCapacity ??
          data.prevManualVehicleReg?.abiCC ??
          null,
      });
    } else {
      updateQuoteFields({
        "risk.vehicles.0.previousVehicleRegistrationNumber": "",
        "risk.vehicles.0.previousVehicleOwned12Month": false,
        "risk.vehicles.0.previousVehicleAbicode": "",
        "risk.vehicles.0.previousVehicleYOM": 0,
        "risk.vehicles.0.previousVehicleMake": "",
        "risk.vehicles.0.previousVehicleModel": "",
        "risk.vehicles.0.previousVehicleCubicCapacity": "",
      });
    }

    // Garage Address
    if (!vehicle?.garageAddress) {
      updateQuoteFields({
        "risk.vehicles.0.garageAddress": {
          houseNo:
            data.garageAddress?.houseNo ?? driver?.address?.houseNo ?? null,
          postcode:
            data.garageAddress?.postcode ?? driver?.address?.postcode ?? null,
          street: data.garageAddress?.street ?? driver?.address?.street ?? null,
          county: data.garageAddress?.county ?? driver?.address?.county ?? null,
          locality:
            data.garageAddress?.locality ?? driver?.address?.locality ?? null,
        },
      });
    } else {
      updateQuoteFields({
        "risk.vehicles.0.garageAddress.houseNo":
          data.garageAddress?.houseNo ?? driver?.address?.houseNo ?? null,
        "risk.vehicles.0.garageAddress.postcode":
          data.garageAddress?.postcode ?? driver?.address?.postcode ?? null,
        "risk.vehicles.0.garageAddress.street":
          data.garageAddress?.street ?? driver?.address?.street ?? null,
        "risk.vehicles.0.garageAddress.county":
          data.garageAddress?.county ?? driver?.address?.county ?? null,
        "risk.vehicles.0.garageAddress.locality":
          data.garageAddress?.locality ?? driver?.address?.locality ?? null,
      });
    }

    // Purchase Info
    updateQuoteFields({
      "risk.vehicles.0.purchaseDate":
        !purchaseDate || deformatDateT(purchaseDate) === "30/12/1899"
          ? quote?.risk?.startDate
          : formatDateT(purchaseDate),
    });

    // Other vehicle fields
    updateQuoteFields({
      "risk.vehicles.0.hackneyCarriage": vehicle?.hackneyCarriage ?? false,
      "risk.vehicles.0.cctv": vehicle?.cctv ?? false,
      "risk.vehicles.0.colourId": vehicle?.colourId ?? null,
      "risk.vehicles.0.currentMileage": vehicle?.currentMileage ?? 0,
      "risk.vehicles.0.businessMileage": vehicle?.businessMileage ?? 0,
      "risk.vehicles.0.annualMileage": vehicle?.annualMileage ?? 0,
      "risk.vehicles.0.pricePaid": vehicle?.pricePaid ?? 0,
      "risk.vehicles.0.personalisedPlate": vehicle?.personalisedPlate ?? false,
      "risk.vehicles.0.operatingArea": vehicle?.operatingArea ?? null,
      "risk.vehicles.0.operatingPostcode": vehicle?.operatingPostcode ?? null,
      "risk.vehicles.0.permanentReferenceNumber":
        vehicle?.permanentReferenceNumber ?? 0,
      "risk.vehicles.0.preVehicleSearch": vehicle?.preVehicleSearch ?? null,
      "risk.vehicles.0.preVehicleSearchSuccess":
        vehicle?.preVehicleSearchSuccess ?? null,
      "risk.vehicles.0.qPlatedReasonId": vehicle?.qPlatedReasonId ?? null,
      "risk.vehicles.0.series": vehicle?.series ?? null,
      "risk.vehicles.0.usedForContractWork":
        vehicle?.usedForContractWork ?? false,
      "risk.vehicles.0.valueReasonId": vehicle?.valueReasonId ?? null,
      "risk.vehicles.0.vehicleGrouping": vehicle?.vehicleGrouping ?? null,
      "risk.vehicles.0.countryOfFirstRegistrationId":
        vehicle?.countryOfFirstRegistrationId ?? "0",
      "risk.vehicles.0.exteriorCameraDetails":
        vehicle?.exteriorCameraDetails ?? null,
      "risk.vehicles.0.gvw": vehicle?.gvw ?? 0,
      "risk.vehicles.0.otherNcdDetails": vehicle?.otherNcdDetails ?? null,
      "risk.vehicles.0.parentObjectId": vehicle?.parentObjectId ?? null,
      "risk.vehicles.0.parentObjectHistoryId":
        vehicle?.parentObjectHistoryId ?? 0,
      "risk.vehicles.0.objectId": vehicle?.objectId ?? "",
      "risk.vehicles.0.objectHistoryId": vehicle?.objectHistoryId ?? null,
    });

    // registeredDate: only set if present, no default 2008
    let registeredYear =
      vehicle?.yearOfManufacture && vehicle?.yearOfManufacture !== 1899
        ? vehicle.yearOfManufacture
        : data.lookupvehicleReg?.year ?? data.vehicleYear?.value;
    if (registeredYear) {
      updateQuoteFields({
        "risk.vehicles.0.registeredDate": formatDateT(
          `${registeredYear}-01-01`
        ),
      });
    }

    updateQuoteFields({
      "risk.vehicles.0.uberVehicleId":
        vehicle?.uberVehicleId ??
        "8PGSyf_jtJ_a4k7P9Tp00mTZMLqWhjolUxogXOKeDFaMjIcuqEl5R7oiqY9SxuRTAdSpGhtplv3or_TSqDVSFu8ZzO4re-q3sgE56SSMOCyFT_1PsrQnxM4lw1xGMYpE9Q==",
      "risk.drivers.0.uberDriverId":
        driver?.uberDriverId ?? "UBER_TEST_USER_2_BRAMWELL",
      "risk.uberMetrics.lifetime_completed_trips": "0",
    });
  },
};

export default Tq5Mapper;
